import { AppBar, Avatar, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, Fade, FormControlLabel, Grid, IconButton, InputBase, LinearProgress, List, ListItem, ListItemAvatar, ListItemText, ListSubheader, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Theme, ThemeProvider, Toolbar, Tooltip, Typography, createStyles, makeStyles } from '@material-ui/core';
import { AcUnit, Add, AddToPhotos, ArrowBack, Assignment, Cached, Delete, Done, Edit, FileCopy, GetApp, Publish, Save, Search, Update } from '@material-ui/icons';
import { Alert, Autocomplete, Pagination } from '@material-ui/lab';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import FileUpload from '../../components/FileUpload';
import Logger from '../../components/Logger';
import ResumoDrawer from '../../components/ResumoDrawer';
import { estados } from '../../constants';
import Page from '../../containers/Page/Page';
import { useAuth } from '../../hooks/auth';
import { useLoading } from '../../hooks/loading';
import { usePermissions } from '../../hooks/permissions';
import { api } from '../../services/api';
import { ContextualTheme } from '../../themes';
import { ILog } from '../../types/Logger';
import { IResumo } from '../../types/Resumo';
import { Helpers } from '../../utils/Helpers';

interface IProposta {
    id: string;
    proposta_grupo: string;
    emitente: string;
    depto: string;
    lista_precos: string;
    tipo: string;
    revisao: string;
    segmento_id: string;
    numero: string;
    cliente_nome: string;
    cliente_uf: string;
    contato: string;
    vendedor: string;
    cond_pag: string;
    encargo: string;
    moeda: string;
    data_taxa: string;
    data_taxa_compra: string;
    taxa_usd: string;
    taxa_euro: string;
    taxa_usd_compra: string;
    taxa_euro_compra: string;
    titulo: string;
    finalidade: string;
    ordem_venda: string;
    encargos: string;
    embalagem: string;
    frete: string;
    comissao: string;
    negociacao: string;
    custo_exportacao: string;
    congelado: boolean;
    finalizado: boolean;
    upk_base: string;
    usd_cpp_base: string;
    usd_ksb_base: string;
    ras: string;
    nao_contribuinte: boolean;
}

interface IItem {
    id: string;
    nome: string;
    dummy: boolean;
    pivot: {
        id: string;
        qtd: string;
        lin_sap: string;
        tamanho: string;
        pis_cofins: string;
        icms: string;
        ipi: string;
        desconto_negociacao: string;
        preco_total_un_sem_impostos: string;
        preco_total_sem_impostos: string;
        preco_total_com_impostos_com_ipi: string;
    }
}

interface IModelo {
    id: string;
    nome: string;
}

interface IModeloData {
    data: IModelo[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
}

interface ICondicao {
    id: string;
    cod: string;
    desc: string;
}

interface IVendedor {
    codusuario: string;
    nome: string;
}

interface ICliente {
    nome: string;
}

interface ISegmento {
    id: string;
    segmento: string;
}

interface ITributo {
    campo: string;
    preenchido: boolean;
    disabled: boolean;
    changed: boolean;
    proximo: boolean;
    valor: string;
    valores: {
        cod: string;
        desc: string;
    }[];
}

interface IImposto {
    aliq_icm: string;
    aliq_pis: string;
    aliq_ipi: string;
}

interface IVersao {
    versao_id: string;
    revisao: string;
    revisao_comentario: string;
    selected: boolean;
}

interface IParams {
    id: string;
}

function PropostasCreate() {

    //Hooks    
    const history = useHistory();
    const classes = useStyles();
    const permissions = usePermissions();
    const params = useParams<IParams>();
    const { data } = useAuth();
    const { search } = useLocation();

    //Loading
    const { startLoading, stopLoading, isLoading } = useLoading();

    //Alert
    const { enqueueSnackbar: alert } = useSnackbar();

    //Defaults
    const defaultProposta = {
        id: '',
        proposta_grupo: '',
        emitente: data.user.nome,
        depto: data.user.area,
        lista_precos: data.user.mercado,
        tipo: '',
        segmento_id: '',
        numero: '',
        cliente_nome: '',
        cliente_uf: '',
        contato: '',
        vendedor: '',
        cond_pag: '',
        encargo: '0',
        embalagem: '0',
        frete: '0',
        revisao: '0',
        moeda: data.user.mercado === 'ME' ? 'USD' : 'R$',
        data_taxa: '',
        data_taxa_compra: '',
        taxa_usd: '',
        taxa_euro: '',
        taxa_usd_compra: '',
        taxa_euro_compra: '',
        titulo: '',
        finalidade: '',
        ordem_venda: '',
        encargos: '',
        comissao: '0',
        negociacao: '0',
        custo_exportacao: '0',
        congelado: false,
        finalizado: false,
        upk_base: '',
        usd_cpp_base: '',
        usd_ksb_base: '',
        ras: '',
        nao_contribuinte: false,
    }

    //States
    const [error, setError] = React.useState<string>('');
    const [loadingCount, setLoadingCount] = React.useState<number>(0);
    const [proposta, setProposta] = React.useState<IProposta>(defaultProposta);
    const [itens, setItens] = React.useState<IItem[]>([]);
    const [modeloDialog, setModeloDialog] = React.useState<boolean>(false);
    const [modeloLoading, setModeloLoading] = React.useState<boolean>(false);
    const [modeloSearch, setModeloSearch] = React.useState<string>('');
    const [modelos, setModelos] = React.useState<IModeloData>();
    const [importDialog, setImportDialog] = React.useState<boolean>(false);
    const [file, setFile] = React.useState<File>();
    const [finalizarDialog, setFinalizarDialog] = React.useState<boolean>(false);
    const [ordem, setOrdem] = React.useState<string>('');
    const [resumo, setResumo] = React.useState<IResumo>({} as IResumo);
    const [resumoError, setResumoError] = React.useState<string>('');
    const [resumoDrawer, setResumoDrawer] = React.useState<boolean>(false);
    const [tributos, setTributos] = React.useState<ITributo[]>([]);
    const [tributosAlert, setTributosAlert] = React.useState<boolean>(false);
    const [tributosDialog, setTributosDialog] = React.useState<boolean>(false);
    const [impostos, setImpostos] = React.useState<IImposto>({} as IImposto);
    const [condicoes, setCondicoes] = React.useState<ICondicao[]>([]);
    const [vendedores, setVendedores] = React.useState<IVendedor[]>([]);
    const [impostosEdit, setImpostosEdit] = React.useState<boolean>(false);
    const [clonarDialog, setClonarDialog] = React.useState<boolean>(false);
    const [logs, setLogs] = React.useState<ILog[]>([]);
    const [deleteLoading, setDeleteLoading] = React.useState<string>('');
    const [clientes, setClientes] = React.useState<ICliente[]>([]);
    const [clientesInput, setClientesInput] = React.useState<string>('');
    const [valoresDialog, setValoresDialog] = React.useState<boolean>(false);
    const [valoresLoading, setValoresLoading] = React.useState<boolean>(false);
    const [segmentos, setSegmentos] = React.useState<ISegmento[]>([]);
    const [versoes, setVersoes] = React.useState<IVersao[]>([]);
    const [versoesDialog, setVersoesDialog] = React.useState<boolean>(false);
    const [versoesComentario, setVersoesComentario] = React.useState<string>('');
    const [rasValue, setRasValue] = React.useState<string>('');
    const [rasDialog, setRasDialog] = React.useState<boolean>(false);

    //Constantes
    const loading = loadingCount > 0;
    const searchParams = new URLSearchParams(search);
    const clonado = searchParams.get('clonado') === 'true';

    //Effects
    React.useEffect(() => {
        handleFetchCreate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        handleFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        handleFetchModelos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        handleFetchResumo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    //Funções
    async function handleFetch(loadingIndex: number = 1) {
        if (params.id) {
            setLoadingCount(c => c + loadingIndex);
            try {
                const response = await api.get(`propostas/${params.id}`);
                const parsed = response.data.object.tributos_json ? JSON.parse(response.data.object.tributos_json) : {};
                setProposta(response.data.object);
                setVersoes(response.data.object.versoes);
                setOrdem(response.data.object.ordem_venda);
                setClientesInput(response.data.object.cliente_nome);
                setItens(response.data.object.modelos);
                setLogs(response.data.object.logs);
                if (parsed.tributos) setTributos(parsed.tributos);
                setImpostos({
                    aliq_icm: response.data.object.aliq_icm,
                    aliq_ipi: response.data.object.aliq_ipi,
                    aliq_pis: response.data.object.aliq_pis
                });
                setError('');
            } catch (error: any) {
                setError(Helpers.errorFormat(error));
            } finally {
                setLoadingCount(c => c - loadingIndex);
            }
        }
    }

    async function handleFetchResumo() {
        if (params.id) {
            try {
                const response = await api.get(`resumo/geral/${params.id}`);
                setResumo(response.data.object);
                setResumoError('');
            } catch (error: any) {
                setResumoError(Helpers.errorFormat(error));
            }
        }
    }

    async function handleFetchCreate() {
        setLoadingCount(c => c + 1);
        try {
            const response = await api.get('propostas/recursos');
            setCondicoes(response.data.object.condicoes);
            setVendedores(response.data.object.vendedores);
            setClientes(response.data.object.clientes);
            setSegmentos(response.data.object.segmentos);
            if (!params.id) {
                setProposta({
                    ...proposta,
                    ras: response.data.object.ras,
                    data_taxa: response.data.object.configs.data_venda_base,
                    data_taxa_compra: response.data.object.configs.data_compra_base,
                    taxa_usd: response.data.object.configs.usd_venda_base,
                    taxa_usd_compra: response.data.object.configs.usd_compra_base,
                    taxa_euro: response.data.object.configs.euro_venda_base,
                    taxa_euro_compra: response.data.object.configs.euro_compra_base,
                });
            }
        } catch (error: any) {
            Helpers.error(error, alert);
        } finally {
            setLoadingCount(c => c - 1);
        }
    }

    async function handleFetchTributos(trb: ITributo[] = tributos, prp: IProposta = proposta) {
        startLoading('tributos');
        try {
            const response = await api.post('propostas/impostos', {
                tributos: [...trb],
                impostos: impostos,
                proposta: {
                    finalidade: prp.finalidade,
                    uf_destino: prp.cliente_uf,
                    nao_contribuinte: prp.nao_contribuinte,
                    proposta_id: params.id
                },
            });

            const { object } = response.data;

            if (object && object.tributos) setTributos(object.tributos);
            if (object && object.impostos) setImpostos(object.impostos);

            if (params.id) {
                await api.put(`propostas/${params.id}`, {
                    ...prp,
                    ...getTributosValues(object.tributos),
                    ...object.impostos,
                    tributos_json: JSON.stringify({
                        tributos: object.tributos,
                        impostos: object.impostos,
                    })
                });
            }
        } catch (error: any) {
            setTributos([]);
            Helpers.error(error, alert);
        } finally {
            stopLoading('tributos');
        }
    }

    async function handleFetchTributosHeader(trb: ITributo[] = tributos, prp: IProposta = proposta) {
        startLoading('tributos');
        try {
            let response = await api.post('propostas/impostos', {
                tributos: [...trb],
                impostos: impostos,
                proposta: {
                    finalidade: prp.finalidade,
                    uf_destino: prp.cliente_uf,
                    nao_contribuinte: prp.nao_contribuinte,
                    proposta_id: params.id
                },
            });

            if (response.data.object && Boolean(response.data.object.impostos.reafunila) === true) {
                response = await api.post('propostas/impostos', {
                    tributos: [],
                    impostos: impostos,
                    proposta: {
                        finalidade: prp.finalidade,
                        uf_destino: prp.cliente_uf,
                        nao_contribuinte: prp.nao_contribuinte,
                        proposta_id: params.id
                    },
                });
                if (params.id) setTributosAlert(true);
            }

            const { object } = response.data;

            if (object && object.tributos) setTributos(object.tributos);
            if (object && object.impostos) setImpostos(object.impostos);

            if (params.id) {
                await api.put(`propostas/${params.id}`, {
                    ...prp,
                    ...getTributosValues(object.tributos),
                    ...object.impostos,
                    tributos_json: JSON.stringify({
                        tributos: object.tributos,
                        impostos: object.impostos,
                    })
                });
            }
        } catch (error: any) {
            setTributos([]);
            Helpers.error(error, alert);
        } finally {
            stopLoading('tributos');
        }
    }

    async function handleFetchModelos(page: number = 1) {
        setModeloLoading(true);
        try {
            const response = await api.get(`modelos?search=${modeloSearch}&page=${page}&tipo=BOMBA&ativa=1`);
            setModelos(response.data.object);
        } catch (error) {
            Helpers.error(error, alert);
        }
        setModeloLoading(false);
    }

    async function handleChangeTributos(index: number, value: string) {
        if (params.id) setTributosAlert(false);
        let newTributos = [...tributos.map(t => { return { ...t, changed: false } })];
        newTributos[index] = { ...newTributos[index], preenchido: true, changed: true, valor: value };
        setTributos(newTributos);
        await handleFetchTributos(newTributos);
    }

    async function handleChangeTributosHeader(campo: keyof IProposta, value: any) {
        let newProposta = { ...proposta, [campo]: value };
        setProposta(newProposta);
        await handleFetchTributosHeader(tributos, newProposta);
    }

    async function handleApplyTributosToAll() {
        setTributosDialog(false);
        startLoading('tributos');
        try {
            const response = await api.post(`propostas/copyimposto/${params.id}`);
            alert(response.data.message, { variant: 'success' });
        } catch (error: any) {
            Helpers.error(error, alert);
        } finally {
            stopLoading('tributos');
        }
    }

    async function handleChangeCondicao(value: string) {
        try {
            const response = await api.get(`propostas/encargo/${value}`);
            setProposta({ ...proposta, cond_pag: value, encargo: response.data });
        } catch (error: any) {
            Helpers.error(error, alert);
        }
    }

    async function handleCongelar() {
        setLoadingCount(c => c + 1);
        try {
            const response = await api.post(`propostas/${params.id}/congelar`);
            setProposta({ ...proposta, congelado: !proposta.congelado });
            alert(response.data.message, { variant: 'success' });
        } catch (error) {
            Helpers.error(error, alert);
        } finally {
            setLoadingCount(c => c - 1);
        }
    }

    async function handleImport() {
        if (file) {
            setImportDialog(false);
            setLoadingCount(c => c + 1);
            try {
                const form = new FormData();
                form.append('memorial', file);
                form.append('_method', 'PUT');
                const response = await api.post(`propostas/${params.id}/memorial`, form);
                handleFetch();
                alert(response.data.message, { variant: 'success' });
            } catch (error) {
                Helpers.error(error, alert);
            } finally {
                setLoadingCount(c => c - 1);
                handleFetchResumo();
            }
        }
    }

    async function handleMemorial() {
        setLoadingCount(c => c + 1);
        try {
            const response = await api.get(`propostas/${params.id}/memorial`, {
                headers: { ...api.defaults.headers, 'Content-Type': 'application/octet-stream' },
                responseType: 'blob'
            });
            Helpers.download(response, `Memorial_${proposta.ras}_${moment().format('YYYYMMDD')}.xlsx`);
        } catch (error: any) {
            let file = new Blob([error.response.data], { type: 'application/json' });
            let json = JSON.parse(await file.text());
            alert(json.message, { variant: 'error' });
        } finally {
            setLoadingCount(c => c - 1);
        }
    }

    async function handleClonar() {
        setClonarDialog(false);
        setLoadingCount(c => c + 1);
        try {
            const response = await api.post(`propostas/clonar/${params.id}`);
            alert(response.data.message, { variant: 'success' });
            history.push(`/propostas/${response.data.id}?clonado=true`);
            window.location.reload();
        } catch (error) {
            Helpers.error(error, alert);
        } finally {
            setLoadingCount(c => c - 1);
        }
    }

    async function handleFinalizar() {
        if (ordem) {
            setFinalizarDialog(false);
            setLoadingCount(c => c + 1);
            try {
                const response = await api.post(`propostas/${params.id}/finalizar`, { ordem_venda: ordem });
                setProposta({ ...proposta, finalizado: true, congelado: true, ordem_venda: ordem });
                alert(response.data.message, { variant: 'success' });
            } catch (error) {
                Helpers.error(error, alert);
            } finally {
                setLoadingCount(c => c - 1);
            }
        }
    }

    async function handleVersionar() {
        if (versoesComentario) {
            setVersoesDialog(false);
            setLoadingCount(c => c + 1);
            try {
                const response = await api.post(`propostas/versionar/${params.id}/`, {
                    revisao_comentario: versoesComentario
                });
                alert(response.data.message, { variant: 'success' });
                history.push(`/propostas/${response.data.id}`);
                window.location.reload();
            } catch (error) {
                Helpers.error(error, alert);
            } finally {
                setLoadingCount(c => c - 1);
            }
        }
    }

    async function handleAtualizar() {
        setValoresDialog(false);
        setValoresLoading(true);
        try {
            const response = await api.put(`propostas/${params.id}/base`);
            setProposta({
                ...proposta,
                upk_base: response.data.object.upk_base,
                usd_ksb_base: response.data.object.usd_ksb_base,
                usd_cpp_base: response.data.object.usd_cpp_base
            });
            alert(response.data.message, { variant: 'success' });
        } catch (error) {
            Helpers.error(error, alert);
        }
        setValoresLoading(false);
    }

    async function handleSave() {
        if (!loading) {
            setLoadingCount(c => c + 1);
            try {
                const response = await api.request({
                    url: params.id ? `propostas/${params.id}` : 'propostas',
                    method: params.id ? 'PUT' : 'POST',
                    data: {
                        ...proposta,
                        ...getTributosValues(),
                        ...impostos,
                        tributos_json: JSON.stringify({
                            tributos: tributos,
                            impostos: impostos
                        })
                    },
                });
                alert(response.data.message, { variant: 'success' });
                if (!params.id) { history.push(`/propostas/${response.data.id}`); }
            } catch (error) {
                Helpers.error(error, alert);
            } finally {
                setLoadingCount(c => c - 1);
            }
        }
    }

    async function handleAutoSave() {
        try {
            await api.put(`propostas/${params.id}`, {
                ...proposta,
                ...getTributosValues(),
                ...impostos,
                tributos_json: JSON.stringify({
                    tributos: tributos,
                    impostos: impostos
                })
            });
        } catch (error) {
            Helpers.error(error, alert);
        }
    }

    async function handleChangeRAS() {
        startLoading('ras');
        try {
            const response = await api.put(`propostas/ras`, {
                proposta_id: params.id,
                ras: rasValue,
            });
            setProposta({ ...proposta, ras: rasValue });
            setRasDialog(false);
            alert(response.data.message, { variant: 'success' });
        } catch (error) {
            Helpers.error(error, alert);
        } finally {
            stopLoading('ras');
        }
    }

    function handleModelosKeyUp(event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) {
        if (event.key === 'Enter') {
            handleFetchModelos();
        }
    }

    function handleNavigate(item: IItem) {
        const type = item.dummy ? 'dummy' : 'modelo';
        history.push(`/propostas/${params.id}/${type}/${item.pivot.id}`);
    }

    function handleChangeCliente(value: string) {
        setClientesInput(value);
        setProposta({ ...proposta, cliente_nome: value });
    }

    function handleChangeVersao(versao: string) {
        history.push(`/propostas/${versao}`);
        window.location.reload();
    }

    async function handleClonarItem(item: IItem) {
        startLoading(`clonar-item-${item.pivot.id}`);
        try {
            const response = await api.post(`propostas/clonar/${params.id}/${item.pivot.id}`);
            alert(response.data.message, { variant: 'success' });
            await handleFetch(0);
        } catch (error) {
            Helpers.error(error, alert);
        } finally {
            stopLoading(`clonar-item-${item.pivot.id}`);
        }
    }

    async function handleDelete(item: IItem) {
        if (!deleteLoading) {
            setDeleteLoading(item.pivot.id);
            try {
                const response = await api.delete(`propostas/excluir/${item.pivot.id}`);
                let newItens = itens.filter(i => i.pivot.id !== item.pivot.id);
                setItens(newItens);
                alert(response.data.message, { variant: 'success' });
            } catch (error) {
                Helpers.error(error, alert);
            }
            setDeleteLoading('');
        }
    }

    async function selectModelo(modelo: IModelo) {
        setModeloLoading(true);
        try {
            const response = await api.post(`propostas/${params.id}/modelo`, {
                modelo_id: modelo.id
            });
            history.push(`/propostas/${params.id}/${response.data.dummy ? 'dummy' : 'modelo'}/${response.data.object}`);
        } catch (error) {
            Helpers.error(error, alert);
        }
        setModeloLoading(false);
    }

    function getTributosValues(trb: ITributo[] = tributos) {
        const values = trb.reduce((total, current) => { total[current.campo] = current.valor; return total }, {} as any);
        return values;
    }

    function getTributosHighlight(tributo: ITributo) {
        if (tributo && tributo.proximo) return { notchedOutline: classes.highlight };
        else return {};
    }

    function getTotalEncargos() {
        return (getValueEncargos(proposta.encargo)
            + getValueEncargos(proposta.embalagem)
            + getValueEncargos(proposta.frete)
            + getValueEncargos(proposta.comissao)
            + getValueEncargos(proposta.negociacao)
            + getValueEncargos(proposta.custo_exportacao)).toFixed(2);
    }

    function getValueEncargos(value: string) {
        let newValue = value ? Number.parseFloat(value.toString().replace(/,/g, '.')) || 0 : 0;
        return newValue;
    }

    return (
        <Page error={error} loading={loading}>
            <Page.Title>
                <Box display="flex" flexDirection="row" alignItems="center">
                    <IconButton size="small" color="primary" className={classes.backButton} onClick={() => history.push('/propostas')}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h6" component="div">
                        {params.id
                            ? `Proposta ${proposta.ras} ${proposta.ordem_venda ? ` - Ordem de Venda ${proposta.ordem_venda}` : ''}`
                            : `Nova Proposta (${proposta.ras})`
                        }
                    </Typography>
                    {permissions.has('proposta.updateRAS') &&
                        <Box ml={1}>
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={() => {
                                    setRasValue(proposta.ras);
                                    setRasDialog(true);
                                }}
                            >
                                <Edit />
                            </IconButton>
                            <Dialog open={rasDialog} fullWidth={true} maxWidth="xs" onClose={() => setRasDialog(false)}>
                                <DialogTitle>Editar Número</DialogTitle>
                                <DialogContent>
                                    <TextField
                                        margin="dense"
                                        label="Número da Proposta"
                                        autoFocus={true}
                                        fullWidth={true}
                                        value={rasValue ?? ''}
                                        onChange={(event) => setRasValue(event.target.value)}
                                    />
                                </DialogContent>
                                <DialogActions>
                                    <Button disabled={isLoading('ras')} onClick={() => setRasDialog(false)}>
                                        Cancelar
                                    </Button>
                                    <Button
                                        color="primary"
                                        variant="contained"
                                        disabled={isLoading('ras')}
                                        startIcon={isLoading('ras') ? <CircularProgress color="inherit" size={16} /> : undefined}
                                        onClick={() => handleChangeRAS()}
                                    >
                                        Salvar
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        </Box>
                    }
                </Box>
                <Box display="flex" alignItems="center">
                    {params.id ?
                        <React.Fragment>
                            <Box>
                                <Button
                                    color="default"
                                    variant="contained"
                                    size="small"
                                    startIcon={<Assignment />}
                                    onClick={() => setResumoDrawer(true)}>
                                    Resumo
                                </Button>
                                <ResumoDrawer
                                    data={resumo}
                                    consolidado={true}
                                    open={resumoDrawer}
                                    error={resumoError}
                                    onClose={() => setResumoDrawer(false)}
                                />
                            </Box>
                            <Box ml={2}>
                                <Logger
                                    logs={logs}
                                    size="small"
                                />
                            </Box>
                            {permissions.has('proposta.congela') &&
                                <Box ml={2}>
                                    <Button
                                        color="secondary"
                                        variant="contained"
                                        size="small"
                                        startIcon={<AcUnit />}
                                        onClick={() => handleCongelar()}>
                                        {proposta.congelado ? 'Descongelar' : 'Congelar'}
                                    </Button>
                                </Box>
                            }
                            {permissions.has('memorial.update') &&
                                <Box ml={2}>
                                    <Button
                                        color="secondary"
                                        variant="contained"
                                        size="small"
                                        disabled={Boolean(!proposta.congelado)}
                                        startIcon={<Publish />}
                                        onClick={() => setImportDialog(true)}>
                                        Importar
                                    </Button>
                                    <FileUpload
                                        open={importDialog}
                                        title="Importar Memorial"
                                        onClick={() => handleImport()}
                                        onDrop={(files) => setFile(files[0])}
                                        onClose={() => setImportDialog(false)}
                                    />
                                </Box>
                            }
                            <Box ml={2}>
                                <Tooltip arrow={true} title={!proposta.congelado ? 'Não é possível exportar pois a proposta não está congelada' : ''}>
                                    <span>
                                        <Button
                                            color="secondary"
                                            variant="contained"
                                            size="small"
                                            disabled={Boolean(!proposta.congelado)}
                                            startIcon={<GetApp />}
                                            onClick={() => handleMemorial()}>
                                            Exportar
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Box>
                            {permissions.has('proposta.clona') &&
                                <Box ml={2}>
                                    <Button
                                        color="primary"
                                        variant="contained"
                                        size="small"
                                        startIcon={<FileCopy />}
                                        onClick={() => setClonarDialog(true)}>
                                        Clonar
                                    </Button>
                                    <Dialog open={clonarDialog} fullWidth={true} maxWidth="xs" onClose={() => setClonarDialog(false)}>
                                        <DialogTitle>Clonar</DialogTitle>
                                        <DialogContent>
                                            <DialogContentText>
                                                Deseja clonar a proposta completa?
                                            </DialogContentText>
                                        </DialogContent>
                                        <DialogActions>
                                            <Button onClick={() => setClonarDialog(false)} color="primary">
                                                Cancelar
                                            </Button>
                                            <Button onClick={() => handleClonar()} color="primary">
                                                Clonar
                                            </Button>
                                        </DialogActions>
                                    </Dialog>
                                </Box>
                            }
                            {permissions.has('proposta.finaliza') &&
                                <Box ml={2}>
                                    <Button
                                        color="primary"
                                        variant="contained"
                                        size="small"
                                        startIcon={proposta.finalizado ? <Edit /> : <Done />}
                                        onClick={() => setFinalizarDialog(true)}>
                                        {proposta.finalizado ? 'Editar OV' : 'Finalizar'}
                                    </Button>
                                    <Dialog open={finalizarDialog} fullWidth={true} maxWidth="xs" onClose={() => setFinalizarDialog(false)}>
                                        <DialogTitle>{proposta.finalizado ? 'Editar OV' : 'Finalizar'}</DialogTitle>
                                        <DialogContent>
                                            {!proposta.finalizado &&
                                                <DialogContentText>
                                                    Ao finalizar a proposta, a mesma será congelada permanentemente e não permitirá mais alterações.
                                                </DialogContentText>
                                            }
                                            <TextField
                                                margin="dense"
                                                label="Ordem de Venda"
                                                autoFocus={true}
                                                fullWidth={true}
                                                value={ordem}
                                                onChange={(event) => setOrdem(event.target.value)}
                                            />
                                        </DialogContent>
                                        <DialogActions>
                                            <Button onClick={() => setFinalizarDialog(false)} color="primary">
                                                Cancelar
                                            </Button>
                                            <Button onClick={() => handleFinalizar()} color="primary">
                                                {proposta.finalizado ? 'Atualizar' : 'Finalizar'}
                                            </Button>
                                        </DialogActions>
                                    </Dialog>
                                </Box>
                            }
                            {permissions.has('proposta.update') &&
                                <Box ml={2}>
                                    <Button
                                        color="primary"
                                        variant="contained"
                                        size="small"
                                        startIcon={<Save />}
                                        onClick={() => handleSave()}>
                                        Salvar
                                    </Button>
                                </Box>
                            }
                        </React.Fragment>
                        :
                        <Button
                            color="primary"
                            variant="contained"
                            startIcon={<Save />}
                            onClick={() => handleSave()}>
                            Salvar
                        </Button>
                    }
                </Box>
            </Page.Title>
            {params.id && versoes.length > 0 &&
                <Page.Title>
                    <Grid container justifyContent="flex-end" spacing={2}>
                        <Grid item xs={12} md={3}>
                            <Box display="flex" flexDirection="row" alignItems="flex-end">
                                <TextField
                                    id="versao"
                                    name="versao"
                                    label="Versão"
                                    size="small"
                                    variant="standard"
                                    fullWidth={true}
                                    select={true}
                                    value={params.id || ''}
                                    onChange={(event) => handleChangeVersao(event.target.value)}
                                >
                                    {versoes && versoes.map((versao, index) => (
                                        <MenuItem key={index} value={versao.versao_id}>{`Versão ${versao.revisao} - ${versao.revisao_comentario}`}</MenuItem>
                                    ))}
                                </TextField>
                                {permissions.has('proposta.versiona') &&
                                    <Box ml={2}>
                                        <Button
                                            color="secondary"
                                            variant="contained"
                                            size="small"
                                            disabled={Boolean(proposta.finalizado)}
                                            startIcon={<Cached />}
                                            onClick={() => setVersoesDialog(true)}>
                                            Versionar
                                        </Button>
                                        <Dialog open={versoesDialog} fullWidth={true} maxWidth="xs" onClose={() => setVersoesDialog(false)}>
                                            <DialogTitle>Versionar</DialogTitle>
                                            <DialogContent>
                                                <DialogContentText>
                                                    Escreva um comentário para a versão.
                                                </DialogContentText>
                                                <TextField
                                                    margin="dense"
                                                    label="Comentário da Versão"
                                                    autoFocus={true}
                                                    fullWidth={true}
                                                    value={versoesComentario}
                                                    onChange={(event) => setVersoesComentario(event.target.value)}
                                                />
                                            </DialogContent>
                                            <DialogActions>
                                                <Button onClick={() => setVersoesDialog(false)} color="primary">
                                                    Cancelar
                                                </Button>
                                                <Button onClick={() => handleVersionar()} color="primary">
                                                    Criar Versão
                                                </Button>
                                            </DialogActions>
                                        </Dialog>
                                    </Box>
                                }
                            </Box>
                        </Grid>
                    </Grid>
                </Page.Title>
            }
            <Page.Section>
                <Page.Content>
                    <Box p={2}>
                        <Box mb={2}>
                            <Typography variant="h6" component="div">
                                Cabeçalho
                            </Typography>
                            <Typography variant="caption" component="div">
                                Informações principais da proposta
                            </Typography>
                        </Box>
                        <Grid className={classes.row} container spacing={2}>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    id="emitente"
                                    name="emitente"
                                    label="Emitente"
                                    variant="outlined"
                                    size="small"
                                    fullWidth={true}
                                    disabled={true}
                                    value={proposta.emitente || ''}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    id="depto"
                                    name="depto"
                                    label="Depto"
                                    variant="outlined"
                                    size="small"
                                    fullWidth={true}
                                    disabled={true}
                                    value={proposta.depto || ''}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    id="lista_precos"
                                    name="lista_precos"
                                    label="Lista de Preços"
                                    size="small"
                                    variant="outlined"
                                    fullWidth={true}
                                    select={true}
                                    disabled={true}
                                    value={proposta.lista_precos || ''}
                                >
                                    <MenuItem value="MI">Mercado Interno</MenuItem>
                                    <MenuItem value="ME">Mercado Externo</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    id="titulo"
                                    name="titulo"
                                    label="Título do Projeto"
                                    variant="outlined"
                                    size="small"
                                    fullWidth={true}
                                    disabled={!permissions.has('proposta.update')}
                                    value={proposta.titulo || ''}
                                    onChange={(event) => setProposta({ ...proposta, titulo: event.target.value })}
                                    onBlur={params.id ? () => handleAutoSave() : undefined}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    id="moeda"
                                    name="moeda"
                                    label="Moeda"
                                    size="small"
                                    variant="outlined"
                                    fullWidth={true}
                                    select={true}
                                    disabled={!permissions.has('proposta.update')}
                                    value={proposta.moeda || ''}
                                    onChange={(event) => setProposta({ ...proposta, moeda: event.target.value })}
                                    onBlur={params.id ? () => handleAutoSave() : undefined}
                                >
                                    <MenuItem value="R$">R$</MenuItem>
                                    <MenuItem value="EUR">EUR</MenuItem>
                                    <MenuItem value="USD">USD</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    id="taxa_usd_compra"
                                    name="taxa_usd_compra"
                                    label="Câmbio Compra USD"
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    fullWidth={true}
                                    disabled={!permissions.has('proposta.update')}
                                    value={proposta.taxa_usd_compra || ''}
                                    onChange={(event) => setProposta({ ...proposta, taxa_usd_compra: event.target.value })}
                                    onBlur={params.id ? () => handleAutoSave() : undefined}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    id="taxa_euro_compra"
                                    name="taxa_euro_compra"
                                    label="Câmbio Compra EUR"
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    fullWidth={true}
                                    disabled={!permissions.has('proposta.update')}
                                    value={proposta.taxa_euro_compra || ''}
                                    onChange={(event) => setProposta({ ...proposta, taxa_euro_compra: event.target.value })}
                                    onBlur={params.id ? () => handleAutoSave() : undefined}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    id="data_taxa_compra"
                                    name="data_taxa_compra"
                                    label="Câmbio Compra Data"
                                    variant="outlined"
                                    size="small"
                                    type="date"
                                    fullWidth={true}
                                    disabled={!permissions.has('proposta.update')}
                                    value={proposta.data_taxa_compra || ''}
                                    onChange={(event) => setProposta({ ...proposta, data_taxa_compra: event.target.value })}
                                    onBlur={params.id ? () => handleAutoSave() : undefined}
                                />
                            </Grid>
                            {condicoes && condicoes.length > 0 &&
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        id="cond_pag"
                                        name="cond_pag"
                                        label="Cond. Pagto"
                                        variant="outlined"
                                        size="small"
                                        fullWidth={true}
                                        select={true}
                                        disabled={!permissions.has('proposta.update')}
                                        value={proposta.cond_pag || ''}
                                        onChange={(event) => handleChangeCondicao(event.target.value)}
                                        onBlur={params.id ? () => handleAutoSave() : undefined}
                                    >
                                        {condicoes.map((cond, index) => (
                                            <MenuItem key={index} value={cond.id}>{`${cond.cod}-${cond.desc}`}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                            }
                            <Grid item xs={12} md={3}>
                                <TextField
                                    id="taxa_usd"
                                    name="taxa_usd"
                                    label="Câmbio Venda USD"
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    fullWidth={true}
                                    disabled={!permissions.has('proposta.update')}
                                    value={proposta.taxa_usd || ''}
                                    onChange={(event) => setProposta({ ...proposta, taxa_usd: event.target.value })}
                                    onBlur={params.id ? () => handleAutoSave() : undefined}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    id="taxa_euro"
                                    name="taxa_euro"
                                    label="Câmbio Venda EUR"
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    fullWidth={true}
                                    disabled={!permissions.has('proposta.update')}
                                    value={proposta.taxa_euro || ''}
                                    onChange={(event) => setProposta({ ...proposta, taxa_euro: event.target.value })}
                                    onBlur={params.id ? () => handleAutoSave() : undefined}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    id="data_taxa"
                                    name="data_taxa"
                                    label="Câmbio Venda Data"
                                    variant="outlined"
                                    size="small"
                                    type="date"
                                    fullWidth={true}
                                    disabled={!permissions.has('proposta.update')}
                                    value={proposta.data_taxa || ''}
                                    onChange={(event) => setProposta({ ...proposta, data_taxa: event.target.value })}
                                    onBlur={params.id ? () => handleAutoSave() : undefined}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Autocomplete
                                    options={clientes}
                                    freeSolo={true}
                                    disabled={(params.id && !clonado ? true : false) || !permissions.has('proposta.update')}
                                    clearText="Limpar"
                                    getOptionLabel={(option) => option.nome}
                                    onInputChange={(_e, value) => handleChangeCliente(value)}
                                    onBlur={params.id ? () => handleAutoSave() : undefined}
                                    inputValue={clientesInput || ''}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            id="cliente_nome"
                                            name="cliente_nome"
                                            label="Cliente"
                                            variant="outlined"
                                            size="small"
                                            fullWidth={true}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    id="cliente_uf"
                                    name="cliente_uf"
                                    label="UF de Destino"
                                    variant="outlined"
                                    size="small"
                                    fullWidth={true}
                                    select={true}
                                    disabled={!permissions.has('proposta.update')}
                                    value={proposta.cliente_uf || ''}
                                    onChange={(event) => handleChangeTributosHeader('cliente_uf', event.target.value)}
                                >
                                    <ListSubheader disableSticky>OUTROS</ListSubheader>
                                    <MenuItem value="EXPORTAÇÃO">EXPORTAÇÃO</MenuItem>
                                    <ListSubheader disableSticky>BRASIL</ListSubheader>
                                    {estados && estados.map((estado, index) => (
                                        <MenuItem key={index} value={estado}>{estado}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    id="finalidade"
                                    name="finalidade"
                                    label="Finalidade"
                                    size="small"
                                    variant="outlined"
                                    fullWidth={true}
                                    select={true}
                                    disabled={!permissions.has('proposta.update')}
                                    value={proposta.finalidade || ''}
                                    onChange={(event) => handleChangeTributosHeader('finalidade', event.target.value)}
                                >
                                    <MenuItem value="Industrialização">Industrialização</MenuItem>
                                    <MenuItem value="Consumo">Consumo</MenuItem>
                                </TextField>
                            </Grid>
                            {segmentos && segmentos.length > 0 &&
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        id="segmento"
                                        name="segmento"
                                        label="Segmento"
                                        size="small"
                                        variant="outlined"
                                        fullWidth={true}
                                        select={true}
                                        disabled={!permissions.has('proposta.update')}
                                        value={proposta.segmento_id || ''}
                                        onChange={(event) => setProposta({ ...proposta, segmento_id: event.target.value })}
                                        onBlur={params.id ? () => handleAutoSave() : undefined}
                                    >
                                        {segmentos.map((segmento, index) => (
                                            <MenuItem key={index} value={segmento.id}>
                                                {segmento.segmento}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                            }
                            {vendedores && vendedores.length > 0 &&
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        id="vendedor"
                                        name="vendedor"
                                        label="Vendedor"
                                        variant="outlined"
                                        size="small"
                                        fullWidth={true}
                                        select={true}
                                        disabled={!permissions.has('proposta.update')}
                                        value={proposta.vendedor || ''}
                                        onChange={(event) => setProposta({ ...proposta, vendedor: event.target.value })}
                                        onBlur={params.id ? () => handleAutoSave() : undefined}
                                    >
                                        {vendedores.map((vendedor, index) => (
                                            <MenuItem key={index} value={vendedor.codusuario}>{vendedor.nome}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                            }
                            {params.id &&
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        id="contato"
                                        name="contato"
                                        label="Contato"
                                        variant="outlined"
                                        size="small"
                                        fullWidth={true}
                                        disabled={!permissions.has('proposta.update')}
                                        value={proposta.contato || ''}
                                        onChange={(event) => setProposta({ ...proposta, contato: event.target.value })}
                                        onBlur={params.id ? () => handleAutoSave() : undefined}
                                    />
                                </Grid>
                            }
                            <Grid item xs={12} md={4}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={Boolean(proposta.nao_contribuinte)}
                                            name="nao_contribuinte"
                                            color="secondary"
                                            onChange={(event) => handleChangeTributosHeader('nao_contribuinte', event.target.checked)}
                                        />
                                    }
                                    label="Não contribuinte de ICMS"
                                />
                            </Grid>
                        </Grid>
                        {tributosAlert &&
                            <Fade in={tributosAlert}>
                                <Box mb={2}>
                                    <Alert severity="warning" style={{ alignItems: 'center' }}>
                                        É necessário reespecificar os impostos devido a alteração do cabeçalho.
                                    </Alert>
                                </Box>
                            </Fade>
                        }
                        <Box mb={1}>
                            <Divider></Divider>
                        </Box>
                        <Box mb={2}>
                            <Typography variant="h6" component="div">
                                Encargos
                            </Typography>
                            <Typography variant="caption" component="div">
                                Definição dos custos adicionais que serão incluídos como padrão nas linhas de bomba
                            </Typography>
                        </Box>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    id="encargo"
                                    name="encargo"
                                    label="Encargos Financeiros (%)"
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    fullWidth={true}
                                    disabled={true}
                                    value={proposta.encargo ?? ''}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    id="embalagem"
                                    name="embalagem"
                                    label="Encargos Embalagem (%)"
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    fullWidth={true}
                                    disabled={!permissions.has('proposta.update')}
                                    value={proposta.embalagem ?? ''}
                                    onChange={(event) => setProposta({ ...proposta, embalagem: event.target.value })}
                                    onBlur={params.id ? () => handleAutoSave() : undefined}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    id="frete"
                                    name="frete"
                                    label="Encargos Frete (%)"
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    fullWidth={true}
                                    disabled={!permissions.has('proposta.update')}
                                    value={proposta.frete ?? ''}
                                    onChange={(event) => setProposta({ ...proposta, frete: event.target.value })}
                                    onBlur={params.id ? () => handleAutoSave() : undefined}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    id="comissao"
                                    name="comissao"
                                    label="Encargos Comissão (%)"
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    fullWidth={true}
                                    disabled={!permissions.has('proposta.update')}
                                    value={proposta.comissao ?? ''}
                                    onChange={(event) => setProposta({ ...proposta, comissao: event.target.value })}
                                    onBlur={params.id ? () => handleAutoSave() : undefined}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    id="negociacao"
                                    name="negociacao"
                                    label="Especial % (Negociação)"
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    fullWidth={true}
                                    disabled={!permissions.has('proposta.update')}
                                    value={proposta.negociacao ?? ''}
                                    onChange={(event) => setProposta({ ...proposta, negociacao: event.target.value })}
                                    onBlur={params.id ? () => handleAutoSave() : undefined}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    id="custo_exportacao"
                                    name="custo_exportacao"
                                    label="Custo Exportação (%)"
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    fullWidth={true}
                                    disabled={!permissions.has('proposta.update')}
                                    value={proposta.custo_exportacao ?? ''}
                                    onChange={(event) => setProposta({ ...proposta, custo_exportacao: event.target.value })}
                                    onBlur={params.id ? () => handleAutoSave() : undefined}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    id="total"
                                    name="total"
                                    label="Encargos TOTAL %"
                                    variant="outlined"
                                    size="small"
                                    fullWidth={true}
                                    disabled={true}
                                    value={getTotalEncargos()}
                                />
                            </Grid>
                        </Grid>
                        {params.id &&
                            <React.Fragment>
                                <Box my={2}>
                                    <Divider></Divider>
                                </Box>
                                <Box mb={2}>
                                    <Typography variant="h6" component="div">
                                        Valores de Conversão
                                    </Typography>
                                    <Typography variant="caption" component="div">
                                        Definição dos valores utilizados para conversão de moedas na proposta
                                    </Typography>
                                </Box>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            id="upk_base"
                                            name="upk_base"
                                            label="Base UPK"
                                            variant="outlined"
                                            size="small"
                                            fullWidth={true}
                                            disabled={true}
                                            value={proposta.upk_base || ''}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            id="usd_ksb_base"
                                            name="usd_ksb_base"
                                            label="Base USD KSB"
                                            variant="outlined"
                                            size="small"
                                            fullWidth={true}
                                            disabled={true}
                                            value={proposta.usd_ksb_base || ''}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            id="usd_cpp_base"
                                            name="usd_cpp_base"
                                            label="Base USD CPP"
                                            variant="outlined"
                                            size="small"
                                            fullWidth={true}
                                            disabled={true}
                                            value={proposta.usd_cpp_base || ''}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <Button
                                            style={{ height: '100%' }}
                                            color="default"
                                            variant="contained"
                                            startIcon={valoresLoading ? <CircularProgress color="inherit" size="15px" /> : <Update />}
                                            fullWidth={true}
                                            disabled={valoresLoading || !permissions.has('proposta.update')}
                                            onClick={() => setValoresDialog(true)}>
                                            Atualizar
                                        </Button>
                                        <Dialog open={valoresDialog} onClose={() => setValoresDialog(false)}>
                                            <DialogTitle>Atualizar</DialogTitle>
                                            <DialogContent>
                                                <DialogContentText>
                                                    Se você atualizar, os valores atuais serão perdidos, deseja mesmo atualizar com os valores atuais da configuração?
                                                </DialogContentText>
                                            </DialogContent>
                                            <DialogActions>
                                                <Button onClick={() => setValoresDialog(false)} color="primary">
                                                    Cancelar
                                                </Button>
                                                <Button onClick={() => handleAtualizar()} color="primary">
                                                    Atualizar
                                                </Button>
                                            </DialogActions>
                                        </Dialog>
                                    </Grid>
                                </Grid>
                            </React.Fragment>
                        }
                        {tributos && tributos.length > 0 &&
                            <React.Fragment>
                                <Box my={2}>
                                    <Box mb={1}>
                                        <Divider></Divider>
                                    </Box>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={9}>
                                            <Box flexDirection="row" display="flex" alignItems="center">
                                                <Box mr={1}>
                                                    <Typography variant="h6" component="div">
                                                        Impostos
                                                    </Typography>
                                                </Box>
                                                {isLoading('tributos') && <CircularProgress size={12} color="inherit" />}
                                            </Box>
                                            <Typography variant="caption" component="div">
                                                Definição dos impostos incluídos como padrão nas linhas de bomba
                                            </Typography>
                                        </Grid>

                                    </Grid>
                                </Box>
                                <Grid className={classes.row} container spacing={2}>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            id="uf_origem"
                                            name="uf_origem"
                                            label="UF de Origem"
                                            variant="outlined"
                                            size="small"
                                            fullWidth={true}
                                            select={true}
                                            InputProps={{ classes: getTributosHighlight(tributos[0]) }}
                                            disabled={(tributos[0] ? Boolean(tributos[0].disabled) : false) || !permissions.has('proposta.update') || isLoading('tributos') || !proposta.cliente_uf || !proposta.finalidade}
                                            value={tributos[0] ? tributos[0].valor || '' : ''}
                                            onChange={(event) => handleChangeTributos(0, event.target.value)}
                                        >
                                            {tributos[0] ? tributos[0].valores.map((valor, index) => (
                                                <MenuItem key={index} value={valor.cod}>{`${valor.cod}`}</MenuItem>
                                            )) : <MenuItem></MenuItem>}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            id="codimposto"
                                            name="codimposto"
                                            label="Cód. Imposto"
                                            variant="outlined"
                                            size="small"
                                            fullWidth={true}
                                            select={true}
                                            InputProps={{ classes: getTributosHighlight(tributos[1]) }}
                                            disabled={(tributos[1] ? Boolean(tributos[1].disabled) : false) || !permissions.has('proposta.update') || isLoading('tributos')}
                                            value={tributos[1] ? tributos[1].valor || '' : ''}
                                            onChange={(event) => handleChangeTributos(1, event.target.value)}
                                        >
                                            {tributos[1] ? tributos[1].valores.map((valor, index) => (
                                                <MenuItem key={index} value={valor.cod}>{`${valor.cod} - ${valor.desc}`}</MenuItem>
                                            )) : <MenuItem></MenuItem>}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            id="cfop"
                                            name="cfop"
                                            label="CFOP"
                                            variant="outlined"
                                            size="small"
                                            fullWidth={true}
                                            select={true}
                                            InputProps={{ classes: getTributosHighlight(tributos[2]) }}
                                            disabled={(tributos[2] ? Boolean(tributos[2].disabled) : false) || !permissions.has('proposta.update') || isLoading('tributos')}
                                            value={tributos[2] ? tributos[2].valor || '' : ''}
                                            onChange={(event) => handleChangeTributos(2, event.target.value)}
                                        >
                                            {tributos[2] ? tributos[2].valores.map((valor, index) => (
                                                <MenuItem key={index} value={valor.cod}>{`${valor.cod} - ${valor.desc}`}</MenuItem>
                                            )) : <MenuItem></MenuItem>}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            id="dir_fisc_icms"
                                            name="dir_fisc_icms"
                                            label="Dir.Fisc.ICMS"
                                            variant="outlined"
                                            size="small"
                                            fullWidth={true}
                                            select={true}
                                            InputProps={{ classes: getTributosHighlight(tributos[3]) }}
                                            disabled={(tributos[3] ? Boolean(tributos[3].disabled) : false) || !permissions.has('proposta.update') || isLoading('tributos')}
                                            value={tributos[3] ? tributos[3].valor || '' : ''}
                                            onChange={(event) => handleChangeTributos(3, event.target.value)}
                                        >
                                            {tributos[3] ? tributos[3].valores.map((valor, index) => (
                                                <MenuItem key={index} value={valor.cod}>{`${valor.cod} - ${valor.desc}`}</MenuItem>
                                            )) : <MenuItem></MenuItem>}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            id="dir_fisc_ipi"
                                            name="dir_fisc_ipi"
                                            label="Dir.Fisc.IPI"
                                            variant="outlined"
                                            size="small"
                                            fullWidth={true}
                                            select={true}
                                            InputProps={{ classes: getTributosHighlight(tributos[4]) }}
                                            disabled={(tributos[4] ? Boolean(tributos[4].disabled) : false) || !permissions.has('proposta.update') || isLoading('tributos')}
                                            value={tributos[4] ? tributos[4].valor || '' : ''}
                                            onChange={(event) => handleChangeTributos(4, event.target.value)}
                                        >
                                            {tributos[4] ? tributos[4].valores.map((valor, index) => (
                                                <MenuItem key={index} value={valor.cod}>{`${valor.cod} - ${valor.desc}`}</MenuItem>
                                            )) : <MenuItem></MenuItem>}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            id="lei_iss"
                                            name="lei_iss"
                                            label="Lei ISS"
                                            variant="outlined"
                                            size="small"
                                            fullWidth={true}
                                            select={true}
                                            InputProps={{ classes: getTributosHighlight(tributos[5]) }}
                                            disabled={(tributos[5] ? Boolean(tributos[5].disabled) : false) || !permissions.has('proposta.update') || isLoading('tributos')}
                                            value={tributos[5] ? tributos[5].valor || '' : ''}
                                            onChange={(event) => handleChangeTributos(5, event.target.value)}
                                        >
                                            {tributos[5] ? tributos[5].valores.map((valor, index) => (
                                                <MenuItem key={index} value={valor.cod}>{`${valor.cod} - ${valor.desc}`}</MenuItem>
                                            )) : <MenuItem></MenuItem>}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            id="lei_cofins"
                                            name="lei_cofins"
                                            label="Lei COFINS"
                                            variant="outlined"
                                            size="small"
                                            fullWidth={true}
                                            select={true}
                                            InputProps={{ classes: getTributosHighlight(tributos[6]) }}
                                            disabled={(tributos[6] ? Boolean(tributos[6].disabled) : false) || !permissions.has('proposta.update') || isLoading('tributos')}
                                            value={tributos[6] ? tributos[6].valor || '' : ''}
                                            onChange={(event) => handleChangeTributos(6, event.target.value)}
                                        >
                                            {tributos[6] ? tributos[6].valores.map((valor, index) => (
                                                <MenuItem key={index} value={valor.cod}>{`${valor.cod} - ${valor.desc}`}</MenuItem>
                                            )) : <MenuItem></MenuItem>}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            id="lei_pis"
                                            name="lei_pis"
                                            label="Lei PIS"
                                            variant="outlined"
                                            size="small"
                                            fullWidth={true}
                                            select={true}
                                            InputProps={{ classes: getTributosHighlight(tributos[7]) }}
                                            disabled={(tributos[7] ? Boolean(tributos[7].disabled) : false) || !permissions.has('proposta.update') || isLoading('tributos')}
                                            value={tributos[7] ? tributos[7].valor || '' : ''}
                                            onChange={(event) => handleChangeTributos(7, event.target.value)}
                                        >
                                            {tributos[7] ? tributos[7].valores.map((valor, index) => (
                                                <MenuItem key={index} value={valor.cod}>{`${valor.cod} - ${valor.desc}`}</MenuItem>
                                            )) : <MenuItem></MenuItem>}
                                        </TextField>
                                    </Grid>
                                </Grid>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={params.id ? 2 : 3}>
                                        <TextField
                                            id="aliq_pis"
                                            name="aliq_pis"
                                            label="PIS/COFINS (%)"
                                            variant="outlined"
                                            size="small"
                                            type="number"
                                            fullWidth={true}
                                            disabled={!impostosEdit || !permissions.has('proposta.update')}
                                            value={impostos.aliq_pis ?? ''}
                                            onChange={(event) => setImpostos({ ...impostos, aliq_pis: event.target.value })}
                                            onBlur={params.id ? () => handleAutoSave() : undefined}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={params.id ? 2 : 3}>
                                        <TextField
                                            id="aliq_icm"
                                            name="aliq_icm"
                                            label="ICMS (%)"
                                            variant="outlined"
                                            size="small"
                                            type="number"
                                            fullWidth={true}
                                            disabled={!impostosEdit || !permissions.has('proposta.update')}
                                            value={impostos.aliq_icm ?? ''}
                                            onChange={(event) => setImpostos({ ...impostos, aliq_icm: event.target.value })}
                                            onBlur={params.id ? () => handleAutoSave() : undefined}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={params.id ? 2 : 3}>
                                        <TextField
                                            id="aliq_ipi"
                                            name="aliq_ipi"
                                            label="IPI (%)"
                                            variant="outlined"
                                            size="small"
                                            type="number"
                                            fullWidth={true}
                                            disabled={!impostosEdit || !permissions.has('proposta.update')}
                                            value={impostos.aliq_ipi ?? ''}
                                            onChange={(event) => setImpostos({ ...impostos, aliq_ipi: event.target.value })}
                                            onBlur={params.id ? () => handleAutoSave() : undefined}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <Button
                                            style={{ height: '100%' }}
                                            color="default"
                                            variant="contained"
                                            startIcon={<Edit />}
                                            fullWidth={true}
                                            disabled={!permissions.has('proposta.update')}
                                            onClick={() => setImpostosEdit(!impostosEdit)}>
                                            Editar Impostos
                                        </Button>
                                    </Grid>
                                    {params.id &&
                                        <Grid item xs={12} md={3}>
                                            <Button
                                                style={{ height: '100%' }}
                                                color="default"
                                                variant="contained"
                                                startIcon={<AddToPhotos />}
                                                fullWidth={true}
                                                disabled={!permissions.has('proposta.update') || isLoading('tributos')}
                                                onClick={() => setTributosDialog(true)}>
                                                ESPELHAR PARA TODOS OS ITENS
                                            </Button>
                                            <Dialog open={tributosDialog} onClose={() => setTributosDialog(false)}>
                                                <DialogTitle>Espelhar Impostos</DialogTitle>
                                                <DialogContent>
                                                    <DialogContentText>
                                                        Deseja mesmo espelhar os impostos para todos os itens da proposta?
                                                    </DialogContentText>
                                                </DialogContent>
                                                <DialogActions>
                                                    <Button onClick={() => setTributosDialog(false)} color="primary">
                                                        Cancelar
                                                    </Button>
                                                    <Button variant="contained" onClick={() => handleApplyTributosToAll()} color="primary" autoFocus>
                                                        Continuar
                                                    </Button>
                                                </DialogActions>
                                            </Dialog>
                                        </Grid>
                                    }
                                </Grid>
                            </React.Fragment>
                        }
                    </Box>
                </Page.Content>
            </Page.Section>
            {params.id &&
                <Page.Section>
                    <Page.Content>
                        <Toolbar className={classes.toolbar}>
                            <Typography variant="h6" component="div">
                                Itens
                            </Typography>
                            {permissions.has('proposta.storeModelo') &&
                                <Button
                                    color="secondary"
                                    variant="contained"
                                    size="small"
                                    startIcon={<Add />}
                                    onClick={() => setModeloDialog(true)}>
                                    Incluir Itens
                                </Button>
                            }
                            <Dialog open={modeloDialog} fullWidth={true} maxWidth="xs">
                                <AppBar style={{ position: 'relative' }}>
                                    <DialogTitle>Adicionar Bomba</DialogTitle>
                                    <Box mx={3} mb={3}>
                                        <Paper className={classes.custom}>
                                            <InputBase
                                                autoFocus
                                                disabled={loading}
                                                className={classes.input}
                                                placeholder="Pesquisa"
                                                onChange={(event) => setModeloSearch(event.target.value)}
                                                onKeyUp={(event) => handleModelosKeyUp(event)}
                                                value={modeloSearch}
                                            />
                                            <IconButton
                                                disabled={loading}
                                                style={{ padding: 10 }}
                                                onClick={() => handleFetchModelos()}
                                            >
                                                <Search />
                                            </IconButton>
                                        </Paper>
                                        <Box mt={2}>
                                            <Grid container justifyContent="center">
                                                <Pagination
                                                    page={modelos?.current_page || 1}
                                                    count={modelos?.last_page || 1}
                                                    onChange={(_event, page) => handleFetchModelos(page)}
                                                    color="secondary"
                                                />
                                            </Grid>
                                        </Box>
                                    </Box>
                                    <Fade in={modeloLoading} timeout={500}>
                                        <LinearProgress color="secondary"></LinearProgress>
                                    </Fade>
                                </AppBar>
                                <DialogContent>
                                    {modelos &&
                                        <List>
                                            {modelos.data && modelos.data.map((modelo, index) => (
                                                <ListItem button key={index} onClick={() => selectModelo(modelo)}>
                                                    <ListItemAvatar>
                                                        <Avatar>
                                                            <Add />
                                                        </Avatar>
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        primary={modelo.nome}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    }
                                </DialogContent>
                                <DialogActions>
                                    <Button color="primary" onClick={() => setModeloDialog(false)}>
                                        Fechar
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        </Toolbar>
                        <TableContainer>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Item</TableCell>
                                        <TableCell>Lin. SAP</TableCell>
                                        <TableCell>Modelo</TableCell>
                                        <TableCell>Tamanho</TableCell>
                                        <TableCell>Qtd.</TableCell>
                                        <TableCell>PIS/CONFINS</TableCell>
                                        <TableCell>ICMS</TableCell>
                                        <TableCell>IPI</TableCell>
                                        <TableCell>Pr.Tot.Un.s/Imp.</TableCell>
                                        <TableCell>Desc. Negociação</TableCell>
                                        <TableCell>Pr.Tot.s/Imp.</TableCell>
                                        <TableCell>Pr.Tot.c/Imp.c/IPI</TableCell>
                                        <TableCell align="center">Ações</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {itens && itens.map((item, index) =>
                                        <TableRow key={index} hover={true}>
                                            <TableCell onClick={() => handleNavigate(item)}>{index + 1}</TableCell>
                                            <TableCell onClick={() => handleNavigate(item)}>{item.pivot.lin_sap}</TableCell>
                                            <TableCell onClick={() => handleNavigate(item)}><Tooltip title={item.dummy ? 'Bomba Genérica' : ''}><Chip label={item.nome} color={item.dummy ? 'primary' : 'default'} /></Tooltip></TableCell>
                                            <TableCell onClick={() => handleNavigate(item)}>{item.pivot.tamanho}</TableCell>
                                            <TableCell onClick={() => handleNavigate(item)}>{item.pivot.qtd}</TableCell>
                                            <TableCell onClick={() => handleNavigate(item)}>{item.pivot.pis_cofins}</TableCell>
                                            <TableCell onClick={() => handleNavigate(item)}>{item.pivot.icms}</TableCell>
                                            <TableCell onClick={() => handleNavigate(item)}>{item.pivot.ipi}</TableCell>
                                            <TableCell onClick={() => handleNavigate(item)}>{item.pivot.preco_total_un_sem_impostos}</TableCell>
                                            <TableCell onClick={() => handleNavigate(item)}>{item.pivot.desconto_negociacao}</TableCell>
                                            <TableCell onClick={() => handleNavigate(item)}>{item.pivot.preco_total_sem_impostos}</TableCell>
                                            <TableCell onClick={() => handleNavigate(item)}>{item.pivot.preco_total_com_impostos_com_ipi}</TableCell>
                                            <TableCell align="center">
                                                <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center">
                                                    <Box mr={1}>
                                                        {isLoading(`clonar-item-${item.pivot.id}`) ?
                                                            <CircularProgress color="primary" size={16} />
                                                            :
                                                            <Tooltip arrow title="Clonar Item">
                                                                <IconButton size="small" color="primary" disabled={!permissions.has('proposta.storeModelo')} onClick={() => handleClonarItem(item)}>
                                                                    <FileCopy />
                                                                </IconButton>
                                                            </Tooltip>
                                                        }
                                                    </Box>
                                                    <ThemeProvider theme={ContextualTheme}>
                                                        {deleteLoading === item.pivot.id ?
                                                            <CircularProgress color="secondary" size={16} />
                                                            :
                                                            <Tooltip arrow title="Excluir">
                                                                <IconButton size="small" color="secondary" disabled={!permissions.has('proposta.exclui')} onClick={() => handleDelete(item)}>
                                                                    <Delete />
                                                                </IconButton>
                                                            </Tooltip>
                                                        }
                                                    </ThemeProvider>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Page.Content>
                </Page.Section>
            }
        </Page>
    );
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        backButton: {
            marginRight: theme.spacing(1),
        },
        row: {
            marginBottom: theme.spacing(1)
        },
        custom: {
            padding: '2px 4px',
            display: 'flex',
            alignItems: 'center',
        },
        input: {
            marginLeft: theme.spacing(1),
            flex: 1,
        },
        toolbar: {
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(1),
            justifyContent: 'space-between',
        },
        title: {
            flex: '1 1 100%',
        },
        highlight: {
            borderColor: theme.palette.secondary.main,
        }
    })
);

export default PropostasCreate;